import {
  Box,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import {
  debounceEventHandler,
  handleErrorMessage,
  last30DaysFromDate,
  last30DaysToDate,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SearchIcon from "assets/icons1/search_icon.svg";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  fetchFailedStats,
  fetchFailedStatsCount,
  GetFailBouncedName,
} from "./ReportsService";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import {
  failedStatsBounceStatusFilter,
  failedStatsCampaignsStatusFilter,
  failedStatsTableHeader,
} from "./ReportsConstants";
import contactsStyles from "screens/Contacts/Contacts.styles";
import { DateRange } from "models/interfaces";
import DashboardDateRangePicker from "screens/Dashboard/Components/DashboardDateRangePicker";
import { useAppSelector } from "utils/hooks";
import { selectIsManager } from "redux/generalSlice";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
    },
  },
};

interface pageFilters {
  page: number;
  rowsPerPage: number;
  searchText: string;
  dropdownBonusValue?: string;
  dropdownCampaignsValue?: string;
}

const initialPageFilterState = (
  page: number,
  rowsPerPage: number,
  searchText: string,
  dropdownBonusValue?: string,
  dropdownCampaignsValue?: string
) => {
  return {
    page,
    rowsPerPage,
    searchText,
    dropdownCampaignsValue: dropdownCampaignsValue,
    dropdownBonusValue: dropdownBonusValue,
  };
};

const FailedStats = () => {
  useTitle(title.FAILED_STATS);
  const theme = useTheme();
  const classes = contactsStyles(theme);
  const isManager = useAppSelector(selectIsManager);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const failedStatsCampaignsTypeFromUrl = urlParams.get("all");
  const failedStatsBonusStatusFilterFromUrl = urlParams.get("SoftBounced");
  const searchFromUrl = urlParams.get("search");
  const [failedStats, setFailedStats] = useState<string[]>([]);
  const [bouncedName, setBouncedName] = useState<any>({});
  const [count, setCount] = useState<number>();
  const [openDateRangePicker, setOpenDateRangePicker] =
    useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: last30DaysFromDate,
    toDate: last30DaysToDate,
  });
  const dateRangeRef = useRef(dateRange);

  const [failedStatsFilters, setFailedStatsFilters] = useState<pageFilters>(
    initialPageFilterState(
      pageFromUrl,
      rowsPerPageFromUrl,
      searchFromUrl ?? "",
      failedStatsBonusStatusFilterFromUrl ?? "9",
      failedStatsCampaignsTypeFromUrl ?? "all"
    )
  );

  const hasContactFetchPermission = hasAccessTo(
    strings.contact,
    strings.fetchPermission
  );

  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange, setDateRange]);

  useEffect(() => {
    getBouncedNameWithNumber();
  }, []);

  useEffect(() => {
    const { page, rowsPerPage, searchText } = failedStatsFilters;
    searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}`
    );
    getTableData(dateRange);
  }, [failedStatsFilters]);

  const getBouncedNameWithNumber = async () => {
    try {
      setIsLoading(true);
      const res = await GetFailBouncedName();
      setBouncedName(res);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleRowsPageData = (rowPerPage: number) => {
    setFailedStatsFilters({
      ...failedStatsFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const handleChangePage = async (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    setFailedStatsFilters({ ...failedStatsFilters, page });
  };

  const handleDropDownChange = (event: any) => {
    const dropdownValue = event.target.value;
    setFailedStatsFilters({
      ...failedStatsFilters,
      page: 1,
      dropdownBonusValue: dropdownValue,
    });
  };

  const handleCampaignsDropDownChange = (event: any) => {
    const dropdownValue = event.target.value;
    setFailedStatsFilters({
      ...failedStatsFilters,
      page: 1,
      dropdownCampaignsValue: dropdownValue,
    });
  };

  const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setFailedStatsFilters({
      ...failedStatsFilters,
      searchText,
      page: 1,
    });
  };

  const getTableData = async (dateRange: DateRange) => {
    const {
      page,
      rowsPerPage,
      searchText,
      dropdownBonusValue,
      dropdownCampaignsValue,
    } = failedStatsFilters;
    try {
      setIsLoading(true);
      const count = await fetchFailedStatsCount(
        dateRange?.fromDate!,
        dateRange?.toDate!,
        searchText,
        dropdownBonusValue,
        dropdownCampaignsValue
      );
      setCount(count);
      await getTableDataAccordingToPage(
        page,
        count,
        dateRange?.fromDate!,
        dateRange?.toDate!,
        rowsPerPage,
        searchText.trim(),
        dropdownBonusValue,
        dropdownCampaignsValue
      );
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getTableDataAccordingToPage = async (
    page: number,
    count: number,
    fromDate: Date | string | null,
    toDate: Date | string | null,
    rowsPerPage: number,
    searchText: string,
    dropdownBonusValue: any,
    dropdownCampaignsValue: any
  ) => {
    try {
      let failedStatsRes: any[] = [];
      failedStatsRes = await fetchFailedStats(
        page,
        rowsPerPage,
        fromDate,
        toDate,
        searchText,
        dropdownBonusValue,
        dropdownCampaignsValue
      );
      const tableData = convertDataToTableFormat(failedStatsRes);
      setFailedStats([...tableData]);
    } catch (error) {
      throw error;
    }
  };

  const convertDataToTableFormat = (data: any) => {
    return data.map((item: any) => {
      return {
        campaignOwner: item.campaignOwner,
        contactEmail: item.contactEmail,
        campaignName: item.campaignName,
        reason: item.reason,
      };
    });
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="contacts_search_box"
            placeholder="Search Failed Stats"
            variant="outlined"
            fullWidth
            onChange={debounceEventHandler(
              handleSearchOnChange,
              strings.SEARCH_TIME_OUT
            )}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
          <Divider />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row">
              <Select
                variant="outlined"
                id="failed_stats_options_dropdown"
                value={failedStatsFilters.dropdownBonusValue!}
                onChange={handleDropDownChange}
                sx={classes.dropDown}
                MenuProps={MenuProps}
              >
                {failedStatsBounceStatusFilter.map((item: any) => (
                  <MenuItem
                    key={item.id}
                    value={bouncedName[item.value] ?? ""}
                    sx={{
                      ...classes.dropDownMenuItem,
                      padding: "12px",
                    }}
                    disabled={isLoading}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
              {isManager && (
                <Select
                  variant="outlined"
                  id="failed_stats_campaigns_dropdown"
                  value={failedStatsFilters.dropdownCampaignsValue}
                  onChange={handleCampaignsDropDownChange}
                  sx={classes.dropDown}
                  MenuProps={MenuProps}
                >
                  {failedStatsCampaignsStatusFilter.map(
                    (item: any, index: number) => (
                      <MenuItem
                        key={index}
                        value={item.value}
                        sx={{
                          ...classes.dropDownMenuItem,
                          padding: "12px",
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    )
                  )}
                </Select>
              )}
            </Stack>
            <DashboardDateRangePicker
              openDateRangePicker={openDateRangePicker}
              setOpenDateRangePicker={setOpenDateRangePicker}
              dateRange={dateRange}
              setDateRange={setDateRange}
              helperFunction={getTableData}
            />
          </Stack>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getHedingSection = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="column">
          <Typography variant="h5" color="text.primary">
            Failed Stats
          </Typography>
        </Stack>
        {getFilters()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={failedStatsTableHeader}
              rows={failedStats}
              page={failedStatsFilters.page}
              rowsPerPage={failedStatsFilters.rowsPerPage}
              onRowsPerPageChange={handleRowsPageData}
              onPageChange={handleChangePage}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={handleChangePage}
              showPagination={true}
              isLoading={isLoading}
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };

  return hasContactFetchPermission ? (
    <CenteredAuthenticatedLayout>
      {getHedingSection()}
    </CenteredAuthenticatedLayout>
  ) : (
    <Unauthorized permissionDenied={hasContactFetchPermission} />
  );
};
export default FailedStats;
